import React, { useMemo, useState } from 'react';
import useFeedbacks from '../hooks/useFeedbacks';
import SectionCard from '../components/SectionCard';
import EmptyState from '../components/EmptyState';
import PillBadge from '../components/PillBadge';

/**
 * MobileFeedbacks — les retours privés interceptés par le pare-feu.
 *
 * Ce qu'un client a écrit AU LIEU de le publier sur Google. C'est la promesse
 * centrale de Revyo, et jusqu'ici l'app n'en affichait que le compteur : le
 * restaurateur devait ouvrir le web pour lire quoi que ce soit.
 *
 * Les non-traités sont remontés en tête : ce sont eux qui appellent une action,
 * et un client mécontent se rattrape dans les heures qui suivent, pas la semaine
 * d'après.
 */

const FILTERS = [
  { id: 'a_traiter', label: 'À traiter' },
  { id: 'traites',   label: 'Traités' },
  { id: 'tous',      label: 'Tous' },
];

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const jours = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (jours === 0) return "aujourd'hui";
    if (jours === 1) return 'hier';
    if (jours < 7) return `il y a ${jours} jours`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return ''; }
}

/** Note ressentie : le RPS quand il existe, sinon l'emoji de satisfaction. */
function ressenti(f) {
  if (typeof f.rps_score === 'number') return `${f.rps_score}/100`;
  const map = { decu: '😞 Déçu', moyen: '😐 Moyen', meh: '😐 Moyen', bad: '😞 Déçu' };
  return map[f.satisfaction] || '—';
}

export default function MobileFeedbacks() {
  const { feedbacks, countTotal, countMois, loading, error, refresh, markTreated } = useFeedbacks();
  const [filter, setFilter] = useState('a_traiter');
  const [busyId, setBusyId] = useState(null);

  const liste = useMemo(() => {
    const base = filter === 'tous'
      ? feedbacks
      : feedbacks.filter(f => (filter === 'traites' ? f.traite : !f.traite));
    // Non-traités d'abord, puis du plus récent au plus ancien.
    return [...base].sort((a, b) => {
      if (!!a.traite !== !!b.traite) return a.traite ? 1 : -1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [feedbacks, filter]);

  const nbATraiter = useMemo(() => feedbacks.filter(f => !f.traite).length, [feedbacks]);

  const handleTreat = async (id) => {
    setBusyId(id);
    await markTreated(id);
    setBusyId(null);
  };

  if (loading) {
    return <div style={styles.page}><div style={styles.loading}>Chargement des feedbacks…</div></div>;
  }

  if (error) {
    return (
      <div style={styles.page}>
        <EmptyState
          emoji="⚠️"
          title="Impossible de charger vos feedbacks"
          subtitle={String(error)}
          action={<button type="button" style={styles.retry} onClick={refresh}>Réessayer</button>}
        />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.h1}>💬 Retours privés</h1>
        <p style={styles.sub}>
          Ces retours vous ont été adressés directement par vos clients.
        </p>
      </header>

      <div style={styles.stats}>
        <div style={styles.stat}>
          <div style={styles.statValue}>{countMois}</div>
          <div style={styles.statLabel}>ce mois-ci</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statValue}>{countTotal}</div>
          <div style={styles.statLabel}>au total</div>
        </div>
        <div style={styles.stat}>
          <div style={{ ...styles.statValue, color: nbATraiter > 0 ? '#C8442A' : '#1E6B36' }}>
            {nbATraiter}
          </div>
          <div style={styles.statLabel}>à traiter</div>
        </div>
      </div>

      <div style={styles.filters}>
        {FILTERS.map(f => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            style={{ ...styles.filter, ...(filter === f.id ? styles.filterActive : null) }}
          >
            {f.label}
            {f.id === 'a_traiter' && nbATraiter > 0 && (
              <span style={styles.filterCount}>{nbATraiter}</span>
            )}
          </button>
        ))}
      </div>

      {liste.length === 0 ? (
        <EmptyState
          emoji={filter === 'a_traiter' ? '✅' : '💬'}
          title={filter === 'a_traiter' ? 'Rien à traiter' : 'Aucun feedback'}
          subtitle={
            filter === 'a_traiter'
              ? 'Tous les retours privés reçus ont été traités.'
              : "Dès qu'un client vous adressera un retour via votre QR code, il apparaîtra ici."
          }
        />
      ) : (
        liste.map(f => (
          <SectionCard key={f.id} style={styles.card}>
            <div style={styles.cardTop}>
              <div>
                <div style={styles.author}>{f.prenom || 'Client anonyme'}</div>
                <div style={styles.meta}>
                  {formatDate(f.created_at)} · {ressenti(f)}
                </div>
              </div>
              <PillBadge
                label={f.traite ? 'Traité' : 'À traiter'}
                variant={f.traite ? 'success' : 'warning'}
              />
            </div>

            <blockquote style={styles.quote}>« {f.texte} »</blockquote>

            <div style={styles.actions}>
              {f.telephone_client && (
                // tel: est le geste le plus direct depuis un téléphone —
                // rappeler vaut mieux qu'un e-mail pour rattraper un client déçu.
                <a href={`tel:${f.telephone_client}`} style={styles.callBtn}>
                  📞 Rappeler
                </a>
              )}
              {!f.traite && (
                <button
                  type="button"
                  style={styles.treatBtn}
                  onClick={() => handleTreat(f.id)}
                  disabled={busyId === f.id}
                >
                  {busyId === f.id ? '…' : '✓ Marquer traité'}
                </button>
              )}
            </div>
          </SectionCard>
        ))
      )}
    </div>
  );
}

const styles = {
  page: { padding: '4px 0 12px' },
  loading: { padding: '40px 0', textAlign: 'center', color: '#6B6B6B', fontSize: 14 },
  header: { padding: '0 4px 12px' },
  h1: { margin: '0 0 4px', fontSize: 21, fontWeight: 800, color: '#1A1A1A' },
  sub: { margin: 0, fontSize: 13.5, lineHeight: 1.5, color: '#6B6B6B' },
  stats: { display: 'flex', gap: 10, marginBottom: 14 },
  stat: {
    flex: 1, background: '#FFFFFF', borderRadius: 14, padding: '12px 10px',
    textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)',
  },
  statValue: { fontSize: 22, fontWeight: 800, color: '#1A1A1A', lineHeight: 1.1 },
  statLabel: { fontSize: 11.5, color: '#6B6B6B', marginTop: 3 },
  filters: { display: 'flex', gap: 8, marginBottom: 12 },
  filter: {
    flex: 1, padding: '8px 10px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.12)',
    background: '#FFFFFF', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
    color: '#6B6B6B', cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 6,
  },
  filterActive: { background: '#1A1A1A', color: '#FFFFFF', borderColor: '#1A1A1A' },
  filterCount: {
    background: '#E8502A', color: '#FFF', borderRadius: 999,
    fontSize: 11, fontWeight: 800, padding: '1px 6px', minWidth: 16,
  },
  card: { marginBottom: 12 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 },
  author: { fontSize: 15, fontWeight: 700, color: '#1A1A1A' },
  meta: { fontSize: 12, color: '#6B6B6B', marginTop: 2 },
  quote: {
    margin: '12px 0', padding: '12px 14px', background: '#FAF8F5',
    borderLeft: '3px solid #E8502A', borderRadius: 8,
    fontSize: 14.5, lineHeight: 1.6, color: '#1A1A1A', fontStyle: 'italic',
  },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  callBtn: {
    flex: 1, textAlign: 'center', padding: '10px 14px', borderRadius: 10,
    background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.14)',
    fontSize: 14, fontWeight: 700, color: '#1A1A1A', textDecoration: 'none',
    minHeight: 20,
  },
  treatBtn: {
    flex: 1, padding: '10px 14px', borderRadius: 10, border: 'none',
    background: '#E8502A', color: '#FFFFFF', fontFamily: 'inherit',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
  },
  retry: {
    padding: '10px 18px', borderRadius: 999, border: 'none',
    background: '#E8502A', color: '#FFF', fontFamily: 'inherit',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
  },
};
